import { Box, Button, Grid, Link, Stack, Typography } from "@mui/material";

import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { FeedGrid } from "../components/AllMattersListFeedGrid";
import { KeyboardArrowLeft } from "@mui/icons-material";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, Link as RouterLink } from "react-router-dom";
import { type HandOffMessage } from "@/types/handOffMessage";
import { AlertBar } from "@/features/acquireMultipleBooks/components/AlertBar";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useFeatureFlags } from "@/configs/featureFlag";
import { ButtonVariantOption, LinkButton } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { ContactUsAlert } from "@/components/ContactUsAlert";

export const AllMattersList: React.FC = () => {
  const COOL_DOWN_TIME = 5;

  // 遷移元の画面からデータを引き継ぐ
  const location = useLocation();

  // FeedGridに更新を指示するためのref
  const ref = useRef<{ reload: () => void }>(null);

  const { allMattersAnalysis: isAllMattersAnalysisFeatureEnabled, mypageTop } =
    useFeatureFlags();

  // メッセージを表示するためのState、 ""の場合表示しない
  const [alertMessage, setAlertMessage] = useState("");
  const [coolDownTime, setCoolDownTime] = useState<number>(0);

  useEffect(() => {
    // 前の画面から引き継いだメッセージを確認する
    const locationState = location.state as Record<string, unknown>;
    if (locationState?.handOffMessage) {
      const message = locationState.handOffMessage as HandOffMessage;
      if (message.command === "ALL_MATTERS_SHOW_ACCEPT_MESSAGE") {
        setAlertMessage(
          "全部事項解析を受け付けました。最新の受付状況を確認するには更新ボタンを押してください"
        );
      }
    }
  }, []);

  useEffect(() => {
    if (coolDownTime <= 0) {
      return;
    }
    const interval = setInterval(() => {
      setCoolDownTime((prev) => prev - 1);
    }, 1_000);

    return () => {
      clearInterval(interval);
    };
  }, [coolDownTime]);

  return (
    <PagePaper>
      {alertMessage !== "" ? (
        <AlertBar
          severity="info"
          message={alertMessage}
          onClose={() => {
            setAlertMessage("");
          }}
        />
      ) : (
        ""
      )}
      <form>
        <Box sx={{ display: "flex" }}>
          {/* ページタイトル */}
          <Box sx={{ display: "flex", mb: 3 }}>
            <LibraryBooksIcon sx={{ mr: 1 }} fontSize="large" />
            <PageTitle>全部事項データ化結果一覧</PageTitle>
          </Box>
          <Box sx={{ ml: 3 }}>
            {coolDownTime ? (
              <Button variant="outlined" disabled>
                残り{coolDownTime}秒
              </Button>
            ) : (
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  ref.current?.reload();
                  setCoolDownTime(COOL_DOWN_TIME);
                }}
              >
                更新
              </Button>
            )}
          </Box>
        </Box>

        <ContactUsAlert />

        {isAllMattersAnalysisFeatureEnabled ? (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs>
              <Stack my={2} direction="row" justifyContent="end" spacing={1}>
                <LinkButton
                  size="large"
                  variant={ButtonVariantOption.Outlined}
                  wrapperSize={SizingWrapperStyle.INHERIT}
                  href="/allmatters/upload"
                  sx={{ "text-align": "center" }}
                >
                  全部事項
                  <br />
                  データ化
                </LinkButton>
              </Stack>
            </Grid>
          </Grid>
        ) : null}

        <SectionPaper>
          <FeedGrid ref={ref} />
          <Box sx={{ mb: 6 }} />
          <Stack
            direction={"row"}
            spacing={4}
            justifyContent={"center"}
            mb={2}
          ></Stack>
        </SectionPaper>

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link
            component={RouterLink}
            to={mypageTop ? "/mypage" : "/feed"}
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>
                {mypageTop ? "マイページに戻る" : "不動産の権利異動検索に戻る"}
              </Typography>
            </Box>
          </Link>
        </Box>
      </form>
    </PagePaper>
  );
};
